(() => {
  if (customElements.get('hc-scheduled-slider')) return;
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  function sydneyTime(value) {
    if (!value.trim()) return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
    if (!match) return NaN;
    const [, year, month, day, hour, minute] = match.map(Number);
    if (year < 2000 || month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return NaN;
    const target = Date.UTC(year, month - 1, day, hour, minute);
    let result = target;
    for (let i = 0; i < 3; i++) {
      const parts = Object.fromEntries(formatter.formatToParts(result).map(part => [part.type, part.value]));
      const wallTime = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
      result += target - wallTime;
    }
    const parts = Object.fromEntries(formatter.formatToParts(result).map(part => [part.type, part.value]));
    return +parts.year === year && +parts.month === month && +parts.day === day && +parts.hour === hour && +parts.minute === minute ? result : NaN;
  }
  class ScheduledSlider extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.wrapper = this.querySelector('.slides');
      this.allSlides = Array.from(this.querySelectorAll('.slide'));
      this.controls = Array.from(this.querySelectorAll('.slider-nav [data-slide-id],.dots [data-slide-id]'));
      this.slides = [];
      this.currentIndex = 0;
      this.editor = this.dataset.designMode === 'true';
      this.schedule = this.allSlides.map(slide => ({
        slide, enabled: slide.dataset.scheduled === 'true',
        start: sydneyTime(slide.dataset.start || ''), end: sydneyTime(slide.dataset.end || '')
      }));
      const listen = (node, event, fn, options = {}) => node.addEventListener(event, fn, { ...options, signal: this.controller.signal });
      this.refreshSchedule();
      listen(this.querySelector('.prev'), 'click', () => { this.show(this.currentIndex - 1); this.resetAuto(); });
      listen(this.querySelector('.next'), 'click', () => { this.show(this.currentIndex + 1); this.resetAuto(); });
      this.controls.forEach(control => {
        const select = () => { this.show(this.slides.findIndex(slide => slide.dataset.slideId === control.dataset.slideId)); this.resetAuto(); };
        listen(control, 'click', select);
        listen(control, 'keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(); } });
      });
      let startX = 0, currentX = 0, dragging = false, suppressClick = false;
      const start = event => {
        if (this.slides.length < 2 || (event.button !== undefined && event.button !== 0)) return;
        dragging = true; suppressClick = false;
        startX = currentX = event.touches ? event.touches[0].clientX : event.clientX;
        clearInterval(this.autoTimer);
        this.wrapper.style.transition = 'none';
      };
      const move = event => {
        if (!dragging) return;
        currentX = event.touches ? event.touches[0].clientX : event.clientX;
        this.wrapper.style.transform = `translateX(${-this.currentIndex * 100 + (currentX - startX) / this.offsetWidth * 100}%)`;
      };
      const end = () => {
        if (!dragging) return;
        dragging = false;
        const distance = currentX - startX;
        suppressClick = Math.abs(distance) > 10;
        this.show(this.currentIndex + (Math.abs(distance) > this.offsetWidth * 0.2 ? (distance > 0 ? -1 : 1) : 0));
        this.resetAuto();
      };
      listen(this.wrapper, 'mousedown', start);
      listen(this.wrapper, 'mousemove', move);
      listen(this.wrapper, 'mouseup', end);
      listen(this.wrapper, 'mouseleave', end);
      listen(this.wrapper, 'touchstart', start, { passive: true });
      listen(this.wrapper, 'touchmove', move, { passive: true });
      listen(this.wrapper, 'touchend', end);
      listen(this.wrapper, 'touchcancel', end);
      listen(this.wrapper, 'click', event => { if (suppressClick) { event.preventDefault(); suppressClick = false; } }, { capture: true });
      listen(document, 'visibilitychange', () => { this.refreshSchedule(); this.resetAuto(); });
      listen(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.slideId === event.detail.blockId);
        if (index >= 0) { this.show(index); clearInterval(this.autoTimer); }
      });
      this.scheduleTimer = setInterval(() => this.refreshSchedule(), 1000);
      this.resetAuto();
    }
    disconnectedCallback() {
      this.controller.abort();
      clearInterval(this.scheduleTimer);
      clearInterval(this.autoTimer);
    }
    refreshSchedule() {
      const now = Date.now();
      const selected = this.slides[this.currentIndex];
      const visible = [];
      this.schedule.forEach(({ slide, enabled, start, end }) => {
        const valid = !Number.isNaN(start) && !Number.isNaN(end) && (start === null || end === null || end > start);
        const active = !enabled || (valid && (start === null || now >= start) && (end === null || now < end));
        slide.hidden = !this.editor && !active;
        if (!slide.hidden) visible.push(slide);
        const note = slide.querySelector('[data-schedule-note]');
        if (note) note.textContent = !valid ? 'Invalid schedule: enter YYYY-MM-DD HH:mm and set the end after the start. Hidden on storefront.' : `Sydney schedule: ${slide.dataset.start || 'no start limit'} → ${slide.dataset.end || 'no end limit'}. ${active ? 'Currently visible' : 'Currently hidden'} on storefront. Shown here for editing.`;
        this.controls.filter(control => control.dataset.slideId === slide.dataset.slideId).forEach(control => { control.hidden = slide.hidden; });
      });
      const changed = visible.length !== this.slides.length || visible.some((slide, index) => slide !== this.slides[index]);
      this.slides = visible;
      this.hidden = !visible.length;
      this.querySelector('.prev').hidden = this.querySelector('.next').hidden = visible.length < 2;
      this.querySelector('.slider-nav').hidden = this.querySelector('.dots').hidden = visible.length < 2;
      if (changed) {
        this.show(Math.max(0, visible.indexOf(selected)), false);
        this.resetAuto();
      }
    }
    show(index, animate = true) {
      if (!this.slides.length) return;
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.wrapper.style.transition = animate ? 'transform 0.4s ease' : 'none';
      this.wrapper.style.transform = `translateX(${-this.currentIndex * 100}%)`;
      const id = this.slides[this.currentIndex].dataset.slideId;
      this.controls.forEach(control => {
        const active = control.dataset.slideId === id;
        control.classList.toggle('active', active);
        control.setAttribute('aria-current', String(active));
      });
      this.slides.forEach((slide, i) => { slide.inert = i !== this.currentIndex; });
    }
    resetAuto() {
      clearInterval(this.autoTimer);
      if (!this.editor && !document.hidden && this.slides.length > 1) this.autoTimer = setInterval(() => this.show(this.currentIndex + 1), 5000);
    }
  }
  customElements.define('hc-scheduled-slider', ScheduledSlider);
})();
